import { MAX_DIFFUSION_PAPER_COPIES } from "@etudescarre/constants";
import { Button, cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  ArrowClockwise as ArrowClockwiseIcon,
  ArrowCounterClockwise as ArrowCounterClockwiseIcon,
  BookmarkSimple as BookmarkSimpleIcon,
  CheckCircle as CheckCircleIcon,
  Cube as CubeIcon,
  FileText as FileTextIcon,
  FloppyDisk as FloppyDiskIcon,
  Trash as TrashIcon,
  UserPlus as UserPlusIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";
import { cellInputClass } from "@/lib/utils/cell-input";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";

type ModeEnvoi = "a_envoyer_par_mail" | "en_main_propre" | "courrier";

/** Modes sélectionnables en construisant le bordereau. */
const MODE_OPTIONS: { label: string; value: ModeEnvoi }[] = [
  { label: "Mail", value: "a_envoyer_par_mail" },
  { label: "Main propre", value: "en_main_propre" },
  { label: "Courrier", value: "courrier" },
];

/** One column per recipient team; each document reaches it through its own
 * transmission, all sharing the column's mode d'envoi. */
type Recipient = {
  recipientTeamId: number;
  teamName: string;
  modeEnvoi: ModeEnvoi | null;
  transmissionIds: number[];
};

/** An eligible recipient team the caller may add to the bordereau. */
type EligibleRecipient = {
  id: number;
  name: string;
  contractType: string;
  /** Outside the documents' pool: only receives the comptes-rendus (N142). */
  comptesRendusOnly: boolean;
};

/** A row: a document version, a standalone maquette version or a
 * compte-rendu (N142). */
type BordDocument = {
  /** `document-<versionId>`, `maquette-<versionId>` or
   * `compte_rendu-<compteRenduId>`. */
  id: string;
  kind: "document" | "maquette" | "compte_rendu";
  /** Version id, or compte-rendu id for a compte-rendu. */
  versionId: number;
  fullname: string | null;
  hasNative: boolean;
  hasPdf: boolean;
  hasIfc: boolean;
  maquette: {
    versionId: number;
    fullname: string | null;
    name: string | null;
    hasNative: boolean;
    hasIfc: boolean;
  } | null;
  /** Recipients and formats taken from the previous diffused version. */
  prefilled: boolean;
};

type Cell = {
  versionTransmissionId: number;
  rowId: string;
  transmissionId: number;
  recipientTeamId: number;
  native: boolean;
  pdf: boolean;
  ifc: boolean;
  paperCopies: number | null;
  comments: string | null;
  nativeAvailable: boolean;
  pdfAvailable: boolean;
  ifcAvailable: boolean;
};

type FileFormat = "native" | "pdf" | "ifc";

type CellAvailability = Record<FileFormat, boolean>;

type CellValue = {
  native: boolean;
  pdf: boolean;
  ifc: boolean;
  /** Paper copies; null = no paper. */
  paperCopies: number | null;
  comments: string | null;
};

const EMPTY_CELL: CellValue = {
  native: false,
  pdf: false,
  ifc: false,
  paperCopies: null,
  comments: null,
};

type BulkField = FileFormat | "paper";

const MAIL: ModeEnvoi = "a_envoyer_par_mail";

const FORMAT_LABELS: Record<FileFormat, string> = {
  native: "Natif",
  pdf: "PDF",
  ifc: "IFC",
};

const MISSING_FILE_TITLES: Record<FileFormat, string> = {
  native: "Aucun fichier natif pour ce document",
  pdf: "Aucun fichier PDF pour ce document",
  ifc: "Aucun fichier IFC pour cette maquette",
};

/** A document offers Natif / PDF and paper; a maquette Natif / IFC, never
 * paper; a compte-rendu its file, as PDF, and paper. */
const ROW_FORMATS: Record<BordDocument["kind"], FileFormat[]> = {
  document: ["native", "pdf"],
  maquette: ["native", "ifc"],
  compte_rendu: ["pdf"],
};

function sameFormats(a: CellValue, b: CellValue) {
  return (
    a.native === b.native &&
    a.pdf === b.pdf &&
    a.ifc === b.ifc &&
    a.paperCopies === b.paperCopies
  );
}

function sameCell(a: CellValue, b: CellValue) {
  return sameFormats(a, b) && a.comments === b.comments;
}

function parsePaperCopies(raw: string): number | null {
  const copies = Number.parseInt(raw, 10);
  return Number.isFinite(copies) && copies >= 1
    ? Math.min(copies, MAX_DIFFUSION_PAPER_COPIES)
    : null;
}

type Baseline = {
  documents: BordDocument[];
  recipients: Recipient[];
  cells: Cell[];
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; baseline: Baseline };

/**
 * Staged (draft) edits, relative to the loaded baseline. `removedDocIds` are
 * the ids of the rows removed from the bordereau, `cellOverrides` the
 * per-cell format/comment edits and `modeOverrides` the per-column (recipient
 * team) mode d'envoi.
 */
type Working = {
  removedDocIds: Set<string>;
  cellOverrides: Map<number, CellValue>;
  modeOverrides: Map<number, ModeEnvoi | null>;
};

function emptyWorking(): Working {
  return {
    removedDocIds: new Set(),
    cellOverrides: new Map(),
    modeOverrides: new Map(),
  };
}

function cloneWorking(w: Working): Working {
  return {
    removedDocIds: new Set(w.removedDocIds),
    cellOverrides: new Map(w.cellOverrides),
    modeOverrides: new Map(w.modeOverrides),
  };
}

type BordereauPrestaGridProps = {
  title: string;
  target: "do" | "presta";
  /** Reports unsaved draft edits to a parent that must not reload the grid. */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Called after the draft was saved or the bordereau validated. */
  onSaved?: () => void;
};

/**
 * Editable bordereau en construction (matrix): the right-hand panel of screens
 * 019 and 020, next to their waiting list.
 *
 * Rows are the document versions and standalone maquette versions the caller
 * pilots, columns the recipient teams they are diffused to. Per cell the user
 * sets the formats (Natif / PDF / paper copies for a document, Natif / IFC for
 * a maquette, only those with a file) and a comment; per column the mode
 * d'envoi (mail / main propre / courrier). A column sent by mail carries no
 * paper. Bulk toggles set a format across a whole row, column, or the whole
 * table.
 * Documents can be removed from the bordereau; every draft edit is
 * undo/redo-able (Annuler / Rétablir) and staged until "Enregistrer le
 * brouillon"; "Valider le bordereau" finalises the draft.
 *
 * The pictogram at the end of a row is filled while its recipients and formats
 * are those taken from the previous diffused version; changing a format of the
 * row empties it.
 *
 * The matrix is sparse: a cell only exists where the document is actually
 * diffused to that recipient — empty cells are left blank and are not editable
 * (a document reaches a new recipient through "Ajouter un destinataire").
 */
export function BordereauPrestaGrid({
  title,
  target,
  onDirtyChange,
  onSaved,
}: BordereauPrestaGridProps) {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [working, setWorking] = useState<Working>(emptyWorking);
  const [past, setPast] = useState<Working[]>([]);
  const [future, setFuture] = useState<Working[]>([]);
  const [busy, setBusy] = useState(false);

  // Eligible recipient teams (performer teams on the caller's own and
  // mandated interventions) that can be added to the bordereau.
  const [recipientPool, setRecipientPool] = useState<EligibleRecipient[]>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(
    null,
  );

  const loadBordereau = useCallback(async () => {
    if (isNil(yardId)) return;
    setState({ status: "loading" });
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/diffusion-bordereau",
        { params: { path: { yardId }, query: { target } } },
      );
      if (error || isNil(data)) {
        setState({
          status: "error",
          error: "Erreur lors du chargement du bordereau",
        });
        return;
      }
      setState({ status: "success", baseline: data.data });
    } catch {
      setState({
        status: "error",
        error: "Erreur lors du chargement du bordereau",
      });
    }
  }, [yardId, target]);

  useEffect(() => {
    void loadBordereau();
  }, [loadBordereau]);

  // Load the eligible recipient pool once. Failure is non-fatal — the
  // adder just stays empty/disabled.
  const loadRecipients = useCallback(async () => {
    if (isNil(yardId)) return;
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/diffusion-queue/recipients",
        { params: { path: { yardId }, query: { target } } },
      );
      if (error || isNil(data)) return;
      setRecipientPool(data.data.recipients ?? []);
    } catch {
      // Non-fatal: leave the pool empty.
    }
  }, [yardId, target]);

  useEffect(() => {
    void loadRecipients();
  }, [loadRecipients]);

  const resetDraft = useCallback(() => {
    setWorking(emptyWorking());
    setPast([]);
    setFuture([]);
  }, []);

  // Every draft mutation snapshots the pre-change working state onto `past`
  // (and clears the redo stack) so Annuler / Rétablir can step through them.
  const commit = useCallback((mutate: (w: Working) => void) => {
    setWorking((current) => {
      setPast((p) => [...p, cloneWorking(current)]);
      setFuture([]);
      const next = cloneWorking(current);
      mutate(next);
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const previous = p[p.length - 1];
      setWorking((current) => {
        setFuture((f) => [current, ...f]);
        return previous;
      });
      return p.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      setWorking((current) => {
        setPast((p) => [...p, current]);
        return next;
      });
      return f.slice(1);
    });
  }, []);

  const baseline =
    state.status === "success"
      ? state.baseline
      : { documents: [], recipients: [], cells: [] };

  // Baseline lookups: per-cell values, cell id per (document, recipient), and
  // the baseline mode per column.
  const {
    baselineCell,
    cellIdByKey,
    baselineMode,
    docIdByCellId,
    teamIdByCellId,
    availabilityByCellId,
  } = useMemo(() => {
    const cellMap = new Map<number, CellValue>();
    const idByKey = new Map<string, number>();
    const docByCell = new Map<number, string>();
    const teamByCell = new Map<number, number>();
    const availByCell = new Map<number, CellAvailability>();
    for (const cell of baseline.cells) {
      cellMap.set(cell.versionTransmissionId, {
        native: cell.native,
        pdf: cell.pdf,
        ifc: cell.ifc,
        paperCopies: cell.paperCopies,
        comments: cell.comments,
      });
      teamByCell.set(cell.versionTransmissionId, cell.recipientTeamId);
      idByKey.set(
        `${cell.rowId}:${cell.recipientTeamId}`,
        cell.versionTransmissionId,
      );
      docByCell.set(cell.versionTransmissionId, cell.rowId);
      availByCell.set(cell.versionTransmissionId, {
        native: cell.nativeAvailable,
        pdf: cell.pdfAvailable,
        ifc: cell.ifcAvailable,
      });
    }
    const modeMap = new Map<number, ModeEnvoi | null>();
    for (const r of baseline.recipients) {
      modeMap.set(r.recipientTeamId, r.modeEnvoi);
    }
    return {
      baselineCell: cellMap,
      cellIdByKey: idByKey,
      baselineMode: modeMap,
      docIdByCellId: docByCell,
      teamIdByCellId: teamByCell,
      availabilityByCellId: availByCell,
    };
  }, [baseline]);

  const cellAvailable = useCallback(
    (vtId: number, field: FileFormat): boolean =>
      availabilityByCellId.get(vtId)?.[field] ?? false,
    [availabilityByCellId],
  );

  const effectiveCell = useCallback(
    (vtId: number): CellValue =>
      working.cellOverrides.get(vtId) ?? baselineCell.get(vtId) ?? EMPTY_CELL,
    [working.cellOverrides, baselineCell],
  );

  const effectiveMode = useCallback(
    (teamId: number): ModeEnvoi | null =>
      working.modeOverrides.has(teamId)
        ? (working.modeOverrides.get(teamId) ?? null)
        : (baselineMode.get(teamId) ?? null),
    [working.modeOverrides, baselineMode],
  );

  // A row keeps its "reprise" pictogram until one of its formats changes.
  const reformattedDocIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [vtId, value] of working.cellOverrides) {
      const b = baselineCell.get(vtId);
      const docId = docIdByCellId.get(vtId);
      if (isNotNil(b) && isNotNil(docId) && !sameFormats(b, value)) {
        ids.add(docId);
      }
    }
    return ids;
  }, [working.cellOverrides, baselineCell, docIdByCellId]);

  const isDirty = useMemo(() => {
    if (working.removedDocIds.size > 0) return true;
    for (const [vtId, value] of working.cellOverrides) {
      const b = baselineCell.get(vtId);
      if (isNil(b) || !sameCell(b, value)) return true;
    }
    for (const [teamId, mode] of working.modeOverrides) {
      if ((baselineMode.get(teamId) ?? null) !== mode) return true;
    }
    return false;
  }, [working, baselineCell, baselineMode]);

  useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const visibleDocuments = baseline.documents.filter(
    (d) => !working.removedDocIds.has(d.id),
  );
  const hasMaquetteRow = visibleDocuments.some((d) => d.kind === "maquette");
  // Corner and column toggles: IFC only once a maquette is in the bordereau.
  const bulkFormats: FileFormat[] = hasMaquetteRow
    ? ["native", "pdf", "ifc"]
    : ["native", "pdf"];

  const removeDocument = useCallback(
    (rowId: string) => {
      commit((w) => {
        w.removedDocIds.add(rowId);
      });
    },
    [commit],
  );

  const setCellField = useCallback(
    (vtId: number, patch: Partial<CellValue>) => {
      commit((w) => {
        const current =
          w.cellOverrides.get(vtId) ?? baselineCell.get(vtId) ?? EMPTY_CELL;
        w.cellOverrides.set(vtId, { ...current, ...patch });
      });
    },
    [commit, baselineCell],
  );

  // Collects the visible cell ids in a scope (whole row / column / table) so a
  // bulk toggle can flip a format for all of them at once.
  const scopeCellIds = useCallback(
    (
      scope:
        | { kind: "row"; rowId: string }
        | { kind: "column"; teamId: number }
        | { kind: "all" },
      options: { skipMaquettes?: boolean } = {},
    ): number[] => {
      const ids: number[] = [];
      for (const doc of visibleDocuments) {
        if (options.skipMaquettes && doc.kind === "maquette") continue;
        for (const r of baseline.recipients) {
          if (scope.kind === "row" && doc.id !== scope.rowId) continue;
          if (scope.kind === "column" && r.recipientTeamId !== scope.teamId) {
            continue;
          }
          const vtId = cellIdByKey.get(`${doc.id}:${r.recipientTeamId}`);
          if (isNotNil(vtId)) ids.push(vtId);
        }
      }
      return ids;
    },
    [visibleDocuments, baseline.recipients, cellIdByKey],
  );

  const isMailCell = useCallback(
    (vtId: number) => effectiveMode(teamIdByCellId.get(vtId) ?? 0) === MAIL,
    [effectiveMode, teamIdByCellId],
  );

  const bulkToggle = useCallback(
    (
      scope:
        | { kind: "row"; rowId: string }
        | { kind: "column"; teamId: number }
        | { kind: "all" },
      field: BulkField,
    ) => {
      // Only cells whose version actually carries a file for this format can
      // be toggled — bulk-setting a format on a fileless cell would be rejected
      // (422) on save. Paper skips the maquettes and the columns sent by mail.
      const ids =
        field === "paper"
          ? scopeCellIds(scope, { skipMaquettes: true }).filter(
              (id) => !isMailCell(id),
            )
          : scopeCellIds(scope).filter((id) => cellAvailable(id, field));
      if (ids.length === 0) return;
      // Toggle direction: if every cell already has the format, clear it;
      // otherwise set it on all (one paper copy where there was none).
      const has = (value: CellValue) =>
        field === "paper" ? isNotNil(value.paperCopies) : value[field];
      const allSet = ids.every((id) => has(effectiveCell(id)));
      commit((w) => {
        for (const id of ids) {
          const current =
            w.cellOverrides.get(id) ?? baselineCell.get(id) ?? EMPTY_CELL;
          w.cellOverrides.set(
            id,
            field === "paper"
              ? {
                  ...current,
                  paperCopies: allSet ? null : (current.paperCopies ?? 1),
                }
              : { ...current, [field]: !allSet },
          );
        }
      });
    },
    [
      scopeCellIds,
      effectiveCell,
      commit,
      baselineCell,
      cellAvailable,
      isMailCell,
    ],
  );

  // A column switched to mail drops its paper copies in the same step.
  const setMode = useCallback(
    (teamId: number, mode: ModeEnvoi | null) => {
      const columnIds =
        mode === MAIL ? scopeCellIds({ kind: "column", teamId }) : [];
      commit((w) => {
        w.modeOverrides.set(teamId, mode);
        for (const id of columnIds) {
          const current =
            w.cellOverrides.get(id) ?? baselineCell.get(id) ?? EMPTY_CELL;
          if (isNotNil(current.paperCopies)) {
            w.cellOverrides.set(id, { ...current, paperCopies: null });
          }
        }
      });
    },
    [commit, scopeCellIds, baselineCell],
  );

  const save = useCallback(async () => {
    if (isNil(yardId) || !isDirty) return;
    setBusy(true);
    try {
      const removedRows = baseline.documents.filter((d) =>
        working.removedDocIds.has(d.id),
      );
      if (removedRows.length > 0) {
        const idsOf = (kind: BordDocument["kind"]) =>
          removedRows.filter((d) => d.kind === kind).map((d) => d.versionId);
        // Removed rows go back to the waiting list, without their cells.
        const { error } = await apiClient.DELETE(
          "/api/yards/{yardId}/diffusion-bordereau/documents",
          {
            params: { path: { yardId } },
            body: {
              target,
              versionIds: idsOf("document"),
              maquetteVersionIds: idsOf("maquette"),
              compteRenduIds: idsOf("compte_rendu"),
            },
          },
        );
        if (error) {
          toaster.error({
            title: "Erreur",
            description: "Impossible d'enregistrer le brouillon.",
          });
          return;
        }
      }

      const cells = [...working.cellOverrides.entries()]
        .filter(([vtId, value]) => {
          const docId = docIdByCellId.get(vtId);
          if (isNotNil(docId) && working.removedDocIds.has(docId)) return false;
          const b = baselineCell.get(vtId);
          return isNil(b) || !sameCell(b, value);
        })
        .map(([vtId, value]) => ({
          versionTransmissionId: vtId,
          native: value.native,
          pdf: value.pdf,
          ifc: value.ifc,
          paperCopies: value.paperCopies,
          comments: value.comments,
        }));
      // A column's mode goes to each of its transmissions, except those of
      // the rows just returned to the waiting list.
      const returnedTransmissionIds = new Set(
        baseline.cells
          .filter((cell) => working.removedDocIds.has(cell.rowId))
          .map((cell) => cell.transmissionId),
      );
      const columns = baseline.recipients.flatMap((r) => {
        if (!working.modeOverrides.has(r.recipientTeamId)) return [];
        const mode = working.modeOverrides.get(r.recipientTeamId) ?? null;
        if ((baselineMode.get(r.recipientTeamId) ?? null) === mode) return [];
        return r.transmissionIds
          .filter((id) => !returnedTransmissionIds.has(id))
          .map((transmissionId) => ({ transmissionId, modeEnvoi: mode }));
      });

      if (cells.length > 0 || columns.length > 0) {
        const { error } = await apiClient.PATCH(
          "/api/yards/{yardId}/diffusion-bordereau",
          { params: { path: { yardId } }, body: { target, cells, columns } },
        );
        if (error) {
          toaster.error({
            title: "Erreur",
            description: "Impossible d'enregistrer le brouillon.",
          });
          return;
        }
      }

      toaster.success({
        title: "Brouillon enregistré",
        description: "Les modifications ont été enregistrées.",
      });
      resetDraft();
      await loadBordereau();
      // The pool depends on the comptes-rendus left in the bordereau.
      void loadRecipients();
      onSaved?.();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Impossible d'enregistrer le brouillon.",
      });
    } finally {
      setBusy(false);
    }
  }, [
    yardId,
    isDirty,
    working,
    target,
    baseline,
    baselineCell,
    baselineMode,
    docIdByCellId,
    resetDraft,
    loadBordereau,
    loadRecipients,
    onSaved,
  ]);

  const validate = useCallback(async () => {
    if (isNil(yardId)) return;
    if (isDirty) {
      toaster.error({
        title: "Modifications non enregistrées",
        description:
          "Enregistrez ou annulez vos modifications avant de valider le bordereau.",
      });
      return;
    }
    const confirmed = await confirmDialog({
      title: "Valider le bordereau",
      message: "Les documents diffusés ne pourront plus être modifiés.",
      confirmLabel: "Valider",
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/diffusion-queue/validate",
        { params: { path: { yardId } }, body: { target } },
      );
      if (error || isNil(data)) {
        toaster.error({
          title: "Erreur",
          description: "Impossible de valider le bordereau.",
        });
        return;
      }
      toaster.success({
        title: "Bordereau validé",
        description: isNotNil(data.data.number)
          ? `Le bordereau n° ${data.data.number} a été validé.`
          : "La diffusion a été validée.",
      });
      resetDraft();
      await loadBordereau();
      void loadRecipients();
      onSaved?.();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Impossible de valider le bordereau.",
      });
    } finally {
      setBusy(false);
    }
  }, [
    yardId,
    isDirty,
    target,
    resetDraft,
    loadBordereau,
    loadRecipients,
    onSaved,
  ]);

  // Add a recipient team (from the caller's eligible pool) to the whole
  // bordereau, blocked while the draft is dirty so the added recipient never
  // attaches to a cell the user is about to change. On success, reload the
  // matrix so the new column appears.
  const addRecipient = useCallback(async () => {
    if (isNil(yardId) || isNil(selectedRecipientId)) return;
    if (isDirty) {
      toaster.error({
        title: "Modifications non enregistrées",
        description:
          "Enregistrez ou annulez vos modifications avant d'ajouter un destinataire.",
      });
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/diffusion-queue/recipients",
        {
          params: { path: { yardId } },
          body: { target, recipientTeamId: selectedRecipientId },
        },
      );
      if (error || isNil(data)) {
        toaster.error({
          title: "Erreur",
          description: "Impossible d'ajouter le destinataire.",
        });
        return;
      }
      if (data.data.addedCount === 0) {
        toaster.success({
          title: "Destinataire déjà présent",
          description: "Ce destinataire recevait déjà tous les documents.",
        });
      } else {
        toaster.success({
          title: "Destinataire ajouté",
          description: "Le destinataire a été ajouté au bordereau.",
        });
      }
      setSelectedRecipientId(null);
      await loadBordereau();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Impossible d'ajouter le destinataire.",
      });
    } finally {
      setBusy(false);
    }
  }, [yardId, selectedRecipientId, isDirty, target, loadBordereau]);

  const recipients = baseline.recipients;
  // A document arrives without recipient when no previous version was
  // diffused: it is still a row, waiting for "Ajouter un destinataire".
  const hasContent = visibleDocuments.length > 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {title}
          </h2>
          {state.status === "success" && (
            <p className="mt-1 text-sm text-zinc-500">
              {visibleDocuments.length} document
              {visibleDocuments.length > 1 ? "s" : ""} · {recipients.length}{" "}
              destinataire{recipients.length > 1 ? "s" : ""}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="add-recipient" className="sr-only">
            Ajouter un destinataire
          </label>
          <select
            id="add-recipient"
            value={selectedRecipientId ?? ""}
            onChange={(e) =>
              setSelectedRecipientId(
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            disabled={busy || isDirty || recipientPool.length === 0}
            className="rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm text-zinc-700 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
          >
            <option value="">
              {recipientPool.length === 0
                ? "Aucun destinataire disponible"
                : "Choisir un destinataire…"}
            </option>
            {recipientPool.map((r) => (
              <option key={r.id} value={r.id}>
                {r.comptesRendusOnly
                  ? `${r.name} (comptes-rendus seulement)`
                  : r.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            outline
            onClick={addRecipient}
            disabled={busy || isDirty || isNil(selectedRecipientId)}
            title={
              isDirty
                ? "Enregistrez ou annulez vos modifications avant d'ajouter un destinataire"
                : "Ajouter le destinataire au bordereau"
            }
          >
            <UserPlusIcon size={16} />
            Ajouter un destinataire
          </Button>
          <Button
            type="button"
            outline
            onClick={undo}
            disabled={busy || past.length === 0}
            title="Annuler la dernière action"
          >
            <ArrowCounterClockwiseIcon size={16} />
            Annuler
          </Button>
          <Button
            type="button"
            outline
            onClick={redo}
            disabled={busy || future.length === 0}
            title="Rétablir l'action annulée"
          >
            <ArrowClockwiseIcon size={16} />
            Rétablir
          </Button>
          <Button
            type="button"
            outline
            onClick={save}
            disabled={busy || !isDirty}
          >
            <FloppyDiskIcon size={16} />
            Enregistrer le brouillon
          </Button>
          <Button
            type="button"
            onClick={validate}
            disabled={busy || visibleDocuments.length === 0}
          >
            <CheckCircleIcon size={16} />
            Valider le bordereau
          </Button>
        </div>
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          {/*
            Variable-column grid (one column per recipient), so per the
            screen-design rules horizontal scroll is allowed here; the fixed
            Document column stays sticky on the left, the header stays sticky on
            top, and rows keep a fixed height with truncation.
          */}
          <div className="h-full overflow-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
            <table className="border-collapse text-sm">
              <thead className="sticky top-0 z-20 bg-zinc-50 dark:bg-zinc-900">
                <tr className="border-b border-zinc-200 dark:border-zinc-800">
                  <th className="sticky left-0 z-10 w-64 min-w-64 max-w-64 bg-zinc-50 px-3 py-2 text-left align-top font-medium text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                    <div className="truncate">Document</div>
                    {hasContent && recipients.length > 0 && (
                      <div className="mt-1 flex flex-wrap items-center gap-1 text-xs font-normal text-zinc-500">
                        <span>Tout&nbsp;:</span>
                        {bulkFormats.map((format) => (
                          <FormatToggle
                            key={format}
                            label={FORMAT_LABELS[format]}
                            onClick={() => bulkToggle({ kind: "all" }, format)}
                            disabled={busy}
                          />
                        ))}
                        <FormatToggle
                          label="Papier"
                          onClick={() => bulkToggle({ kind: "all" }, "paper")}
                          disabled={busy}
                        />
                      </div>
                    )}
                  </th>
                  {recipients.map((r) => (
                    <th
                      key={r.recipientTeamId}
                      className="w-56 min-w-56 border-l border-zinc-200 px-3 py-2 text-left align-top font-medium text-zinc-700 dark:border-zinc-800 dark:text-zinc-300"
                    >
                      <div className="truncate" title={r.teamName}>
                        {r.teamName || "(Sans nom)"}
                      </div>
                      <div className="mt-1">
                        <select
                          aria-label={`Mode d'envoi — ${r.teamName}`}
                          value={effectiveMode(r.recipientTeamId) ?? ""}
                          disabled={busy}
                          onChange={(e) =>
                            setMode(
                              r.recipientTeamId,
                              e.target.value === ""
                                ? null
                                : (e.target.value as ModeEnvoi),
                            )
                          }
                          className={cn(
                            "w-full",
                            cellInputClass(
                              working.modeOverrides.has(r.recipientTeamId) &&
                                (baselineMode.get(r.recipientTeamId) ??
                                  null) !== effectiveMode(r.recipientTeamId),
                            ),
                          )}
                        >
                          <option value="">Mode d'envoi</option>
                          {MODE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1 text-xs font-normal text-zinc-500">
                        <span>Colonne&nbsp;:</span>
                        {bulkFormats.map((format) => (
                          <FormatToggle
                            key={format}
                            label={FORMAT_LABELS[format]}
                            onClick={() =>
                              bulkToggle(
                                {
                                  kind: "column",
                                  teamId: r.recipientTeamId,
                                },
                                format,
                              )
                            }
                            disabled={busy}
                          />
                        ))}
                        <FormatToggle
                          label="Papier"
                          onClick={() =>
                            bulkToggle(
                              {
                                kind: "column",
                                teamId: r.recipientTeamId,
                              },
                              "paper",
                            )
                          }
                          disabled={
                            busy || effectiveMode(r.recipientTeamId) === MAIL
                          }
                        />
                      </div>
                    </th>
                  ))}
                  <th
                    className="w-12 min-w-12 border-l border-zinc-200 px-2 py-2 text-center align-top font-medium text-zinc-700 dark:border-zinc-800 dark:text-zinc-300"
                    title="Destinataires et formats repris de la version précédente"
                  >
                    Repris
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.status === "loading" ? (
                  <tr>
                    <td className="px-3 py-8 text-center text-sm text-zinc-500">
                      Chargement...
                    </td>
                  </tr>
                ) : !hasContent ? (
                  <tr>
                    <td className="px-3 py-12 text-center text-sm text-zinc-500">
                      Aucun document dans le bordereau
                    </td>
                  </tr>
                ) : (
                  visibleDocuments.map((doc) => {
                    const isMaquette = doc.kind === "maquette";
                    const docName = doc.fullname ?? "document";
                    // A maquette row only offers the formats it has a file
                    // for; a document row keeps both, greyed when missing.
                    const rowFormats = ROW_FORMATS[doc.kind].filter(
                      (format) =>
                        !isMaquette ||
                        (format === "native" ? doc.hasNative : doc.hasIfc),
                    );
                    return (
                      <tr
                        key={doc.id}
                        className="border-b border-zinc-100 last:border-0 dark:border-zinc-800"
                      >
                        <td className="sticky left-0 z-10 w-64 min-w-64 max-w-64 bg-white px-3 py-2 align-top dark:bg-zinc-950">
                          <div className="flex items-start justify-between gap-2">
                            <span className="flex min-w-0 items-center gap-1.5">
                              {isMaquette && (
                                <CubeIcon
                                  size={16}
                                  className="shrink-0 text-zinc-500"
                                  aria-label="Maquette"
                                />
                              )}
                              {doc.kind === "compte_rendu" && (
                                <FileTextIcon
                                  size={16}
                                  className="shrink-0 text-zinc-500"
                                  aria-label="Compte-rendu, pièces jointes comprises"
                                />
                              )}
                              <span
                                className="truncate"
                                title={doc.fullname ?? undefined}
                              >
                                {doc.fullname ?? "(Sans nom)"}
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeDocument(doc.id)}
                              disabled={busy}
                              title="Retirer du bordereau"
                              aria-label={`Retirer ${doc.fullname ?? "le document"} du bordereau`}
                              className="shrink-0 rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950"
                            >
                              <TrashIcon size={16} />
                            </button>
                          </div>
                          <div className="mt-1 flex items-center gap-2 truncate text-xs text-zinc-600 dark:text-zinc-300">
                            <span title="Formats disponibles dans ÉtudesCarré">
                              {[
                                doc.hasNative && "Natif",
                                doc.hasPdf && "PDF",
                                doc.hasIfc && "IFC",
                              ]
                                .filter(Boolean)
                                .join(" / ") || "-"}
                            </span>
                            {isNotNil(doc.maquette) && (
                              <span
                                className="inline-flex items-center gap-1"
                                title={`Maquette associée : ${doc.maquette.fullname ?? doc.maquette.name ?? "(Sans nom)"}`}
                              >
                                <CubeIcon size={14} aria-hidden />
                                {[
                                  doc.maquette.hasNative && "Natif",
                                  doc.maquette.hasIfc && "IFC",
                                ]
                                  .filter(Boolean)
                                  .join(" / ") || "-"}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-1 text-xs text-zinc-500">
                            <span>Ligne&nbsp;:</span>
                            {rowFormats.map((format) => (
                              <FormatToggle
                                key={format}
                                label={FORMAT_LABELS[format]}
                                onClick={() =>
                                  bulkToggle(
                                    { kind: "row", rowId: doc.id },
                                    format,
                                  )
                                }
                                disabled={busy}
                              />
                            ))}
                            {!isMaquette && (
                              <FormatToggle
                                label="Papier"
                                onClick={() =>
                                  bulkToggle(
                                    { kind: "row", rowId: doc.id },
                                    "paper",
                                  )
                                }
                                disabled={busy}
                              />
                            )}
                          </div>
                        </td>
                        {recipients.map((r) => {
                          const vtId = cellIdByKey.get(
                            `${doc.id}:${r.recipientTeamId}`,
                          );
                          if (isNil(vtId)) {
                            return (
                              <td
                                key={r.recipientTeamId}
                                className="border-l border-zinc-100 bg-zinc-50/40 px-3 py-2 text-center text-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/40"
                              >
                                —
                              </td>
                            );
                          }
                          const value = effectiveCell(vtId);
                          const override = working.cellOverrides.get(vtId);
                          const b = baselineCell.get(vtId);
                          const dirty =
                            isNotNil(override) &&
                            (isNil(b) || !sameCell(b, override));
                          const byMail =
                            effectiveMode(r.recipientTeamId) === MAIL;
                          return (
                            <td
                              key={r.recipientTeamId}
                              className={cn(
                                "border-l border-zinc-100 px-3 py-2 align-top dark:border-zinc-800",
                                dirty && "bg-amber-50 dark:bg-amber-900/20",
                              )}
                            >
                              <div className="flex items-center gap-2">
                                {rowFormats.map((format) => (
                                  <label
                                    key={format}
                                    className="flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-300"
                                    title={
                                      cellAvailable(vtId, format)
                                        ? undefined
                                        : MISSING_FILE_TITLES[format]
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      checked={value[format]}
                                      disabled={
                                        busy || !cellAvailable(vtId, format)
                                      }
                                      aria-label={`${FORMAT_LABELS[format]} — ${docName} vers ${r.teamName}`}
                                      onChange={(e) =>
                                        setCellField(vtId, {
                                          [format]: e.target.checked,
                                        })
                                      }
                                    />
                                    {FORMAT_LABELS[format]}
                                  </label>
                                ))}
                                {isMaquette && rowFormats.length === 0 && (
                                  <span className="text-xs text-zinc-400">
                                    Aucun fichier
                                  </span>
                                )}
                                {!isMaquette && (
                                  <label
                                    className="flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-300"
                                    title={
                                      byMail
                                        ? "Envoi par mail : pas de papier"
                                        : "Nombre de copies papier (vide : pas de papier)"
                                    }
                                  >
                                    Papier
                                    <input
                                      type="number"
                                      min={1}
                                      max={MAX_DIFFUSION_PAPER_COPIES}
                                      value={value.paperCopies ?? ""}
                                      disabled={busy || byMail}
                                      aria-label={`Copies papier — ${docName} vers ${r.teamName}`}
                                      onChange={(e) =>
                                        setCellField(vtId, {
                                          paperCopies: parsePaperCopies(
                                            e.target.value,
                                          ),
                                        })
                                      }
                                      className={cn(
                                        cellInputClass(dirty),
                                        "w-12 px-1 text-xs disabled:opacity-50",
                                      )}
                                    />
                                  </label>
                                )}
                              </div>
                              <input
                                type="text"
                                value={value.comments ?? ""}
                                disabled={busy}
                                onChange={(e) =>
                                  setCellField(vtId, {
                                    comments:
                                      e.target.value === ""
                                        ? null
                                        : e.target.value,
                                  })
                                }
                                placeholder="Commentaire"
                                aria-label="Commentaire"
                                className={cn("mt-1", cellInputClass(dirty))}
                              />
                            </td>
                          );
                        })}
                        <td className="border-l border-zinc-100 px-2 py-2 text-center align-top dark:border-zinc-800">
                          <ReprisePicto
                            checked={
                              doc.prefilled && !reformattedDocIds.has(doc.id)
                            }
                            documentName={doc.fullname ?? "(Sans nom)"}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** End-of-row pictogram: filled while the row is the previous diffusion's. */
function ReprisePicto({
  checked,
  documentName,
}: {
  checked: boolean;
  documentName: string;
}) {
  const label = checked
    ? `${documentName} : destinataires et formats repris de la version précédente`
    : `${documentName} : destinataires et formats non repris de la version précédente`;
  return (
    <BookmarkSimpleIcon
      size={18}
      weight={checked ? "fill" : "regular"}
      role="img"
      aria-label={label}
      className={cn(
        "inline-block",
        checked ? "text-zinc-800 dark:text-zinc-100" : "text-zinc-400",
      )}
    >
      <title>{label}</title>
    </BookmarkSimpleIcon>
  );
}

type FormatToggleProps = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

/** Compact bulk-format toggle used in the corner / column / row headers. */
function FormatToggle({ label, onClick, disabled }: FormatToggleProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={`Basculer ${label} pour cet ensemble`}
      className="rounded border border-zinc-300 px-1 py-0.5 hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
    >
      {label}
    </button>
  );
}
